import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Button,
  DatePicker,
  Divider,
  Form,
  Input,
  notification,
  Select,
  Table,
  Typography,
  Upload,
} from "antd";

import { UploadOutlined } from "@ant-design/icons";

import { getOnlyTrue as getAllCountries } from "../../../api/modules/countries";
import { getAllByCountry } from "../../../api/modules/cities";
import {
  getAll,
  post,
  generateUrl,
} from "../../../api/modules/resources";

import INTERNAL_LINKS from "../../../enum/InternalLinks";

const { Title } = Typography;

const FilesPage = () => {
  const [createForm] = Form.useForm();
  const [countriesData, setCountriesData] = useState([]);
  const [citiesData, setCitiesData] = useState([]);
  const [dataSource, setDataSource] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [level, setLevel] = useState(0);

  const columns = [
    {
      title: "#",
      dataIndex: "id",
      key: "id",
    },
    {
      title: "Level / Nivel",
      dataIndex: "level",
      key: "level",
      render: (_, record) => {
        if (record.level === 1) {
          return "Other / Otro";
        } else {
          return "Country / País";
        }
      },
    },
    {
      title: "Country / Pais",
      dataIndex: "country_name",
      key: "country_name",
      render: (_, record) => {
        if (record.level === 1) {
          return "NA";
        } else {
          return record.country_name;
        }
      },
    },
    {
      title: "City / Ciudad",
      dataIndex: "city_name",
      key: "city_name",
      render: (_, record) => {
        if (record.level === 1) {
          return "NA";
        } else {
          return record.city_name;
        }
      },
    },
    {
      title: "Other / Otro",
      dataIndex: "other",
      key: "other",
      render: (_, record) => {
        if (record.level === 0) {
          return "NA";
        } else {
          return record.other;
        }
      },
    },
    {
      title: "Theme / Temática",
      dataIndex: "topic",
      key: "topic",
    },
    {
      title: "Source / Fuente",
      dataIndex: "source",
      key: "source",
    },
    {
      title: "Year / Año",
      dataIndex: "year",
      key: "year",
    },
    {
      title: "Actions / Acciones",
      render: (_, record) => (
        <>
          <Button style={{ width: "100%" }}>
            <Link to={`${INTERNAL_LINKS.FILE}/${record.id}`}>
              Details / Detalles
            </Link>
          </Button>
          <br></br>
          <br></br>
          <Button
            style={{ width: "100%" }}
            type="primary"
            onClick={() => {
              downloadFile(record.id);
            }}
          >
            Download / Descargar
          </Button>
        </>
      ),
    },
  ];

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    await Promise.all([getCountries(), getData()]);
  };

  const getCountries = async () => {
    const response = await getAllCountries();
    setCountriesData(response.data.results);
  };

  const getCities = async (id) => {
    const response = await getAllByCountry(id);
    setCitiesData(response.data.results);
  };

  const getData = async () => {
    setTableLoading(true);
    try {
      const response = await getAll();
      setDataSource(response.data.results);
    } catch (err) {
      console.log(err);
    }
    setTableLoading(false);
  };

  const downloadFile = async (id) => {
    const response = await generateUrl(id);
    window.open(response.data.result);
  };

  const save = async (data) => {
    setSaveLoading(true);
    try {
      let formData = new FormData();

      formData.append("level", data["level"]);
      if (data.level === 0) {
        formData.append("country", data["country"]);
        if (data.hasOwnProperty("city")) {
          formData.append("city", data["city"]);
        }
      } else {
        formData.append("other", data["other"]);
      }
      formData.append("topic", data["topic"]);
      formData.append("source", data["source"]);
      formData.append("year", data["year"].format("YYYY"));
      formData.append("abstract", data["abstract"]);

      if (data.file.fileList.length > 0) {
        formData.append("resource", data["file"].file);
      }

      const response = await post(formData);
      if ([200, 201, 204].indexOf(response.status) > -1) {
        notification.open({
          message: "Successful transaction / Transacción exitosa ",
          duration: 3,
          placement: "bottomRight",
        });

        createForm.resetFields();

        getData();
      }
    } catch (err) {
      console.log(err);
      notification.error({
        message: "Error transaction / Error en la transacción ",
        duration: 3,
        placement: "bottomRight",
      });
    }
    setSaveLoading(false);
  };

  return (
    <>
      <Title level={3}>Files / Archivos</Title>
      <Divider>
        New elements section / Sección para crear nuevos elementos
      </Divider>
      <Form
        layout="vertical"
        form={createForm}
        onValuesChange={(data) => {
          if (data.hasOwnProperty("country")) {
            getCities(data["country"]);
          }
        }}
        onFinish={save}
        initialValues={{ level }}
      >
        <Form.Item
          label="Level / Nivel"
          name="level"
          rules={[{ required: true }]}
        >
          <Select
            showSearch
            optionFilterProp="children"
            onChange={(value) => {
              setLevel(value);
            }}
          >
            <Select.Option value={0}>Country / País</Select.Option>
            <Select.Option value={1}>Other / Otro</Select.Option>
          </Select>
        </Form.Item>
        {level === 1 ? (
          <Form.Item label="Other" name="other" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
        ) : (
          <>
            <Form.Item
              label="Country / País"
              name="country"
              rules={[{ required: true }]}
            >
              <Select showSearch optionFilterProp="children">
                {countriesData.length > 0 &&
                  countriesData.map((item) => (
                    <Select.Option
                      key={`country-key-${item.id}`}
                      value={item.id}
                    >
                      {item.name}
                    </Select.Option>
                  ))}
              </Select>
            </Form.Item>
            <Form.Item
              label="City / Ciudad"
              name="city"
              rules={[{ required: false }]}
            >
              <Select showSearch optionFilterProp="children">
                {citiesData.length > 0 &&
                  citiesData.map((item) => (
                    <Select.Option key={`city-key-${item.id}`} value={item.id}>
                      {item.name}
                    </Select.Option>
                  ))}
              </Select>
            </Form.Item>
          </>
        )}
        <Form.Item
          label="Theme / Temática"
          name="topic"
          rules={[{ required: true }]}
        >
          <Input></Input>
        </Form.Item>
        <Form.Item
          label="Source / Fuente"
          name="source"
          rules={[{ required: true }]}
        >
          <Input></Input>
        </Form.Item>
        <Form.Item label="Year / Año" name="year" rules={[{ required: true }]}>
          <DatePicker picker="year" />
        </Form.Item>
        <Form.Item
          label="Abstract"
          name="abstract"
          rules={[{ required: true }]}
        >
          <Input.TextArea rows={5}></Input.TextArea>
        </Form.Item>
        <Form.Item label="File / Archivo" name="file">
          <Upload
            multiple={false}
            beforeUpload={() => {
              return false;
            }}
            listType="text"
          >
            <Button icon={<UploadOutlined />}>
              Select file / Seleccione el archivo
            </Button>
          </Upload>
        </Form.Item>
        <Form.Item>
          <Button loading={saveLoading} htmlType="submit" type="primary">
            Save and continue / Guardar y continuar
          </Button>
        </Form.Item>
      </Form>
      <Table
        rowKey="id"
        loading={tableLoading}
        columns={columns}
        dataSource={dataSource}
      ></Table>
      <Button type="primary">
        <Link to={INTERNAL_LINKS.HOME}>Back / Volver</Link>
      </Button>
    </>
  );
};

export default FilesPage;
